/**
 * CtaPanel — Floating CTA side-panel (right edge of screen).
 *
 * Feature-flag: `features.ctaPanel` must be true to render.
 *
 * Behaviour:
 *   • Collapsed: a square glass tab (44 × 44 px) flush with the right edge,
 *     anchored above the menu bar, showing the info icon.
 *   • Expanded: a glass card (220 × 168 px) that slides to horizontal center.
 *     Two CTAs open URLs in a new tab.
 *   • Close animation: content fades out first, then the box shrinks — no squish.
 *   • Auto-open: every 60 s after scene is ready (or after last manual close).
 *
 * URLs:  `intersection.cta.testDriveUrl` / `intersection.cta.infoUrl`
 * Labels: `ui.ctaPanelTestDriveLabel`  / `ui.ctaPanelWebsiteLabel`
 *
 * @see docs/ims-brand-frontends/08-ui-screens.md
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ResolvedConfig } from '@ims/shared';
import { tracker, EVENTS } from '../../services/tracking';

// Cookie-consent–style button tokens (matches CookieConsent.tsx T palette)
const BTN = {
  primaryBg: '#657081',
  primaryHover: '#2c343f',
  primaryBorder: '#657081',
  secondaryBg: '#181d25',
  secondaryHover: '#2a3340',
  secondaryBorder: '#36404e',
  text: '#fcfcfd',
} as const;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const AUTO_OPEN_MS = 60_000;
const AUTO_CLOSE_MS = 20_000;
/** Delay before the CTA panel opens for the first time after the scene is ready. */
const INITIAL_OPEN_MS = 1_500;
const ICON_INFO = '/app/icons/audi-icon-info.svg';

/** How long the content fades out before the box starts shrinking (ms). */
const CONTENT_FADE_OUT_MS = 200;

// ---------------------------------------------------------------------------
// Keyframes
// ---------------------------------------------------------------------------
const KEYFRAMES = `
@keyframes _ctaCloseBtn {
  from { opacity: 0; transform: rotate(-90deg) scale(0.6); }
  to   { opacity: 1; transform: rotate(0deg)   scale(1);   }
}
@keyframes _ctaRipple {
  0%   { transform: scale(0.5); opacity: 0.5; }
  100% { transform: scale(2.2); opacity: 0;   }
}
@keyframes _ctaGlow {
  0%, 100% { opacity: 0.55; }
  50%       { opacity: 1.0;  }
}
@keyframes _ctaIconPulse {
  0%   { opacity: 0.55; }
  35%  { opacity: 1.0;  }
  70%  { opacity: 0.55; }
  100% { opacity: 0.55; }
}
`;
let keyframesInjected = false;
function injectKeyframes() {
  if (keyframesInjected) return;
  keyframesInjected = true;
  const style = document.createElement('style');
  style.textContent = KEYFRAMES;
  document.head.appendChild(style);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
interface CtaPanelProps {
  config: ResolvedConfig | null;
  sceneReady: boolean;
}

export function CtaPanel({ config, sceneReady }: CtaPanelProps) {
  // isOpen  → controls box width/height (structural)
  // showContent → controls expanded-content visibility (cosmetic, leads isOpen on close)
  const [isOpen, setIsOpen] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [viewportH, setViewportH] = useState(() => window.innerHeight);
  const [viewportW, setViewportW] = useState(() => window.innerWidth);

  useEffect(() => {
    const onResize = () => {
      setViewportH(window.innerHeight);
      setViewportW(window.innerWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Ref-indirection to allow scheduleAutoOpen to call itself recursively
  // without violating react-hooks/immutability (no self-reference in useCallback).
  const scheduleAutoOpenRef = useRef<() => void>(() => {});

  injectKeyframes();

  // ── Config ────────────────────────────────────────────────────────────────
  const modelTitle =
    config?.intersection.ctaTitle ??
    [config?.brand.displayName, config?.model.displayName].filter(Boolean).join(' ');
  const testDriveUrl = config?.intersection.cta.testDriveUrl;
  const websiteUrl = config?.intersection.cta.infoUrl;
  const testDriveLabel =
    config?.intersection.cta.testDriveLabel ??
    config?.ui.ctaPanelTestDriveLabel ??
    'Probefahrt buchen';
  const websiteLabel =
    config?.intersection.cta.websiteLabel ?? config?.ui.ctaPanelWebsiteLabel ?? 'Mehr erfahren';

  // ── Auto-open ─────────────────────────────────────────────────────────────
  const scheduleAutoOpen = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    autoTimerRef.current = setTimeout(() => {
      setShowContent(true);
      setIsOpen(true);
      // Auto-close after 20 s, then restart the 60 s cycle via ref to avoid
      // self-reference in the useCallback dependency array.
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = setTimeout(() => {
        setShowContent(false);
        closeTimerRef.current = setTimeout(() => {
          setIsOpen(false);
          scheduleAutoOpenRef.current();
        }, CONTENT_FADE_OUT_MS);
      }, AUTO_CLOSE_MS);
    }, AUTO_OPEN_MS);
  }, []);
  // Keep ref in sync — must be in an effect to satisfy react-hooks/refs
  // (refs must not be written during render).
  useLayoutEffect(() => {
    scheduleAutoOpenRef.current = scheduleAutoOpen;
  }, [scheduleAutoOpen]);

  useEffect(() => {
    if (!sceneReady) return;
    // Open quickly on first scene-ready, then hand off to the regular cycle.
    const initialTimer = setTimeout(() => {
      setShowContent(true);
      setIsOpen(true);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = setTimeout(() => {
        setShowContent(false);
        closeTimerRef.current = setTimeout(() => {
          setIsOpen(false);
          scheduleAutoOpen();
        }, CONTENT_FADE_OUT_MS);
      }, AUTO_CLOSE_MS);
    }, INITIAL_OPEN_MS);
    return () => {
      clearTimeout(initialTimer);
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    };
  }, [sceneReady, scheduleAutoOpen]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleOpen = useCallback(() => {
    if (isOpen) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    setShowContent(true);
    setIsOpen(true);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (autoCloseTimerRef.current) clearTimeout(autoCloseTimerRef.current);
    // 1. Fade content out immediately
    setShowContent(false);
    // 2. Only shrink box after content is gone
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
      scheduleAutoOpen();
    }, CONTENT_FADE_OUT_MS);
  }, [scheduleAutoOpen]);

  // ── Guard ─────────────────────────────────────────────────────────────────
  if (!config?.features.ctaPanel) return null;

  // ── Dimensions ────────────────────────────────────────────────────────────
  const W_COLLAPSED = 42; // matches MenuBar button footprint
  const H_COLLAPSED = 42;
  const W_EXPANDED = 240;
  const H_EXPANDED = 160;

  // Collapsed bottom — responsive to viewport height; lower in landscape
  const isLandscape = viewportW > viewportH;
  const collapsedBottom = isLandscape
    ? Math.min(160, Math.max(100, viewportH - 300))
    : Math.min(200, Math.max(80, viewportH - 230));

  // Horizontal: slide from right edge to viewport horizontal center
  const expandedTranslateX = `calc(-50vw + ${W_EXPANDED / 2}px)`;
  // Vertical: expand symmetrically upward, but clamp so panel top stays ≥ 100px from top
  const maxUpward = Math.max(0, viewportH - collapsedBottom - H_EXPANDED - 100);
  const expandedTranslateY = `-${Math.min((H_EXPANDED - H_COLLAPSED) / 2, maxUpward)}px`;

  return (
    // Outer: position:absolute → clipped by <main> overflow:hidden when footer scrolls in.
    // Stays on the canvas, never floats over the footer.
    <div
      className="absolute right-0 z-40"
      style={{
        bottom: `${collapsedBottom}px`,
        transform: isOpen
          ? `translateX(${expandedTranslateX}) translateY(${expandedTranslateY})`
          : 'translateX(0px) translateY(0px)',
        transition: 'transform 480ms cubic-bezier(0.32, 0.72, 0, 1)',
      }}
    >
      {/* Ripple ring — outside overflow:hidden so it can grow beyond button bounds */}
      {!isOpen && sceneReady && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            width: `${W_COLLAPSED}px`,
            height: `${H_COLLAPSED}px`,
            borderRadius: '14px 0 0 14px',
            border: '1.5px solid rgba(255,255,255,0.4)',
            animation: '_ctaRipple 2s ease-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}
      {/* ── Solid panel — same bg as CookieConsent sheet (#2a3340) ──────── */}
      <div
        onClick={!isOpen ? handleOpen : undefined}
        style={{
          position: 'relative',
          width: isOpen ? `${W_EXPANDED}px` : `${W_COLLAPSED}px`,
          height: isOpen ? `${H_EXPANDED}px` : `${H_COLLAPSED}px`,
          borderRadius: isOpen ? '16px' : '14px 0 0 14px',
          backgroundColor: '#2a3340',
          boxShadow: isOpen
            ? '0 12px 48px rgba(0,0,0,0.70), 0 4px 16px rgba(0,0,0,0.45)'
            : '-4px 0 20px rgba(0,0,0,0.3), 0 0 14px 3px rgba(255,255,255,0.12)',
          overflow: 'hidden',
          cursor: isOpen ? 'default' : 'pointer',
          opacity: sceneReady ? 1 : 0,
          pointerEvents: sceneReady ? 'auto' : 'none',
          transition: [
            'width 420ms cubic-bezier(0.32, 0.72, 0, 1)',
            'height 420ms cubic-bezier(0.32, 0.72, 0, 1)',
            'border-radius 420ms cubic-bezier(0.32, 0.72, 0, 1)',
            'box-shadow 420ms ease',
            'opacity 600ms ease',
          ].join(', '),
        }}
      >
        {/* ── Collapsed tab: info icon ──────────────────────────────────── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: isOpen ? 0 : 1,
            transform: isOpen ? 'scale(0.6)' : 'scale(1)',
            pointerEvents: 'none',
            transition: 'opacity 180ms ease, transform 180ms ease',
          }}
        >
          {/* Ripple ring moved outside overflow:hidden — see above */}
          <img
            src={ICON_INFO}
            width={22}
            height={22}
            alt=""
            style={{
              animation: !isOpen
                ? '_ctaIconPulse 2s cubic-bezier(0.6, 0, 0.8, 1) infinite'
                : undefined,
            }}
          />
        </div>

        {/* ── Expanded content ──────────────────────────────────────────── */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            padding: '14px 14px 16px 14px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            // showContent drives visibility — decoupled from box size
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateX(0px)' : 'translateX(14px)',
            pointerEvents: showContent ? 'auto' : 'none',
            transition: showContent
              ? 'opacity 250ms ease 220ms, transform 280ms ease 180ms' // fade IN: wait for box to open
              : 'opacity 160ms ease, transform 160ms ease', // fade OUT: instant, no delay
          }}
        >
          {/* ✕ Close button + model title ──────────────────────────────── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {modelTitle && (
              <span
                style={{
                  flex: 1,
                  fontSize: 12,
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.70)',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  lineHeight: '24px',
                }}
              >
                {modelTitle}
              </span>
            )}
            <button
              onClick={handleClose}
              aria-label="Schließen"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                height: 24,
                borderRadius: '50%',
                border: 'none',
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: 'rgba(255,255,255,0.85)',
                fontSize: 13,
                lineHeight: 1,
                cursor: 'pointer',
                flexShrink: 0,
                animation: showContent ? '_ctaCloseBtn 350ms ease 300ms both' : 'none',
                transition: 'background-color 150ms ease',
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'rgba(255,255,255,0.30)')
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'rgba(255,255,255,0.18)')
              }
            >
              ✕
            </button>
          </div>

          {/* Buttons ─────────────────────────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Primary CTA: Test drive ─────────────────────────────────────── */}
            {testDriveUrl && (
              <CtaButton
                bg={BTN.primaryBg}
                bgHover={BTN.primaryHover}
                border={BTN.primaryBorder}
                onClick={() => {
                  tracker.track(EVENTS.CTA_TESTDRIVE);
                  window.open(testDriveUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                {testDriveLabel}
              </CtaButton>
            )}

            {/* Secondary CTA: Website ──────────────────────────────────────── */}
            {websiteUrl && (
              <CtaButton
                bg={BTN.secondaryBg}
                bgHover={BTN.secondaryHover}
                border={BTN.secondaryBorder}
                onClick={() => {
                  tracker.track(EVENTS.CTA_WEBSITE);
                  window.open(websiteUrl, '_blank', 'noopener,noreferrer');
                }}
              >
                {websiteLabel}
              </CtaButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CtaButton — inline dark button matching CookieConsent style
// ---------------------------------------------------------------------------
interface CtaBtnProps {
  bg: string;
  bgHover: string;
  border: string;
  onClick: () => void;
  children: React.ReactNode;
}
function CtaButton({ bg, bgHover, border, onClick, children }: CtaBtnProps) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: '100%',
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 50,
        border: `1px solid ${border}`,
        backgroundColor: hovered ? bgHover : bg,
        color: '#fcfcfd',
        fontSize: 14,
        fontWeight: 400,
        letterSpacing: '0.04em',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background-color 150ms ease',
      }}
    >
      {children}
    </button>
  );
}
